import React, { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react';
import { removeUserSession } from '../utils/common';
import { NewContext } from '../utils/ContextProvider';
import useTecnicaPermisos from '../pages/admin/tecnica/useTecnicaPermisos';
import { itemActivo, seccionesMenuTecnica } from '../pages/admin/tecnica/tecnicaMenu';
import '../css/Navbar.css';

/**
 * El menú lateral del modo Técnica: reemplaza al Navbar general en /admin/tecnica/... (Layout.jsx).
 *
 * De PortalAdminSidebar toma la forma de los datos (secciones con ítems) y el marcado de cada
 * ítem, así se ve igual que los portales de Sueldos y Facturación. Del Navbar general toma el
 * contenedor y el colapso: dentro del Layout el margen del contenido depende de `flag` y la
 * hamburguesa del TopNavbar lo cambia, así que un contenedor con su propio `open` quedaría
 * desfasado. No agrega CSS: todo sale de Navbar.css, incluida la versión de celular (cajón).
 */
export default function TecnicaNavbar() {
    const { flag, setFlag, setStates, profileImage, selectedEmployee } = useContext(NewContext);
    const { listo, permisos, aviso } = useTecnicaPermisos();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    // Hasta que vuelva /me sólo va «Menú principal»: si no, los ítems aparecerían y desaparecerían.
    // Con la sesión caída (ERROR_SESION) tampoco: los permisos pueden ser los de antes.
    const secciones = seccionesMenuTecnica(permisos);
    const visibles = listo && aviso !== 'ERROR_SESION' ? secciones : secciones.filter((s) => s.key === 'volver');

    const userName = selectedEmployee
        ? `${selectedEmployee.person?.name || ''} ${selectedEmployee.person?.lastname || ''}`.trim()
        : '';
    const userInitials = selectedEmployee
        ? `${selectedEmployee.person?.name?.[0] || ''}${selectedEmployee.person?.lastname?.[0] || ''}`.toUpperCase()
        : '';

    // En celular el menú es un cajón: al navegar se cierra, igual que en el Navbar general.
    const handleNavClick = () => {
        if (window.innerWidth < 1081) setFlag(!flag);
    };

    // En el teléfono el TopNavbar esconde el avatar: este pie es la única forma de salir.
    const handleLogout = () => {
        setStates();
        removeUserSession();
        navigate('/');
    };

    // El colapso sigue siendo un div para heredar tal cual el estilo de Navbar.css (un <button> trae
    // borde y fondo propios), pero con rol, foco y teclado: quien navega con Tab tiene que poder
    // abrir y cerrar el menú.
    const toggleConTeclado = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setFlag(!flag);
        }
    };

    return (
        <div className={`sidebar ${!flag && 'open'}`}>
            <div
                className="sidebar-collapse-btn"
                role="button"
                tabIndex={0}
                aria-label={flag ? 'Contraer menú' : 'Expandir menú'}
                aria-expanded={!!flag}
                onClick={() => setFlag(!flag)}
                onKeyDown={toggleConTeclado}
            >
                {flag ? <ChevronLeft size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
            </div>
            <div className="contenedor-navbar-items">
                {/* Header de perfil (solo mobile). En el teléfono no hay migas: la línea «Técnica» dice en qué menú está. */}
                <div className="sidebar-profile-header">
                    <div className="sidebar-profile-avatar" onClick={() => { handleNavClick(); navigate('/miperfil'); }}>
                        {profileImage
                            ? <img src={profileImage} alt="perfil" />
                            : <span>{userInitials}</span>
                        }
                    </div>
                    <span className="sidebar-profile-name">{userName}</span>
                    <span className="sidebar-profile-role">Técnica</span>
                </div>

                <nav aria-label="Menú de Técnica" className={flag ? 'list-menu' : 'list-menu-close'}>
                    {visibles.map((seccion) => (
                        <React.Fragment key={seccion.key}>
                            {seccion.title && (
                                <div className={`nav-section-title ${!flag && 'inactive'}`}>{seccion.title}</div>
                            )}
                            {seccion.items.map((item) => {
                                const activo = itemActivo(item, pathname);
                                return (
                                    <Link
                                        key={item.key}
                                        to={item.to}
                                        className={`item-navbar${activo ? ' active' : ''}`}
                                        aria-current={activo ? 'page' : undefined}
                                        title={item.label}
                                        onClick={handleNavClick}
                                    >
                                        <item.Icon size={18} className="icon-flag" />
                                        <p className={!flag ? 'inactive' : ''}>{item.label}</p>
                                    </Link>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    {/* Colapsado, el texto se oculta y el ícono es la única salida: ahí va dentro de un
                        botón para que se pueda usar con teclado. Abierto, el botón es el texto y el ícono
                        es decorativo (si no, serían dos paradas de Tab para lo mismo). */}
                    {flag
                        ? <LogOut size={18} className="icon-flag-session open" onClick={handleLogout} aria-hidden="true" />
                        : (
                            <button type="button" className="logout-button" aria-label="Cerrar Sesión" title="Cerrar Sesión" onClick={handleLogout}>
                                <LogOut size={18} className="icon-flag-session open" aria-hidden="true" />
                            </button>
                        )}
                    <button type="button" className={`logout-button ${!flag && 'inactive'}`} onClick={handleLogout}>Cerrar Sesión</button>
                </div>
            </div>
        </div>
    );
}
